/**
 * Algolia Shared Utilities - Common functions used across Algolia components
 * This file contains shared utilities to reduce code duplication and improve performance
 */

// Prevent redeclaration and multiple script loads
if (typeof window.AlgoliaSharedUtils === 'undefined' && !window.AlgoliaSharedUtilsLoading) {
  window.AlgoliaSharedUtilsLoading = true;
  class AlgoliaSharedUtils {
  /**
   * Wait for InstantSearch to be available from CDN
   * @returns {Promise} Promise that resolves when InstantSearch is available
   */
  static async waitForInstantSearch() {
    return new Promise((resolve) => {
      if (window.instantsearch) {
        resolve(window.instantsearch);
      } else {
        setTimeout(() => resolve(AlgoliaSharedUtils.waitForInstantSearch()), 50);
      }
    });
  }

  /**
   * Format price value with multilingual pricing support
   * @param {number|string} price - Price value to format
   * @param {Object} options - Formatting options
   * @param {HTMLElement} element - Element to read data attributes from
   * @returns {string} Formatted price string
   */
  static formatPrice(price, options = {}, element = null) {
    if (!price || price === '') return '0.00';

    // Clean and parse price
    let numPrice;
    if (typeof price === 'string') {
      const cleanPrice = price.replace(/[€$£¥₹₽₩₪₦₡₨₫₴₸₼₾₿\s]/g, '').replace(/,/g, '.');
      numPrice = parseFloat(cleanPrice);
    } else {
      numPrice = Number(price);
    }

    if (isNaN(numPrice)) return '0.00';

    // Get configuration from element data attributes
    const config = this.getMultilingualPricingConfig(element, options);

    // Determine if price is in cents or main currency BEFORE applying multilingual pricing
    const originalPriceStr = typeof price === 'string' ? price : price.toString();
        const hasDecimalPlaces = originalPriceStr.includes('.') || originalPriceStr.includes(',');

    // More robust detection: if it has decimal places OR is a reasonable main currency amount, treat as main currency
    // Only treat as cents if it's a large whole number without decimal places
    const isOriginalPriceInCents = !hasDecimalPlaces && numPrice > 1000 && numPrice % 1 === 0;

    // Apply multilingual pricing if enabled
    if (this.shouldApplyMultilingualPricing(config)) {
      // Check if price already contains currency symbols (already converted)
      const originalPriceStr = typeof price === 'string' ? price : price.toString();
      const hasCurrencySymbols = /[a-zA-Z]{2,3}\s*$/.test(originalPriceStr) || originalPriceStr.includes('kr') || originalPriceStr.includes('$') || originalPriceStr.includes('€') || originalPriceStr.includes('£');

      if (!hasCurrencySymbols) {
        // Simple conversion: price * rate
        const rate = typeof Shopify.currency.rate === 'string' ? parseFloat(Shopify.currency.rate) : Shopify.currency.rate;
        if (typeof rate === 'number' && !isNaN(rate) && rate > 0 && rate != 1) {
          numPrice = numPrice * rate;

          // Apply currency-specific rounding if enabled
          if (config.enableRoundMultiPrices) {
            // Round up to the nearest whole number keeping in accordance with the default rounding strategy of Shopify
            numPrice = Math.floor(numPrice);
            numPrice = this.applyCurrencySpecificRounding(numPrice, config);
          }
        }
      }
    }

    // Format the price - if original was in cents, convert to main currency units
    let formattedPrice;
    if (isOriginalPriceInCents) {
      formattedPrice = (numPrice / 100).toFixed(2);
    } else {
      formattedPrice = numPrice.toFixed(2);
    }

    // Apply money format template
    if (config.moneyFormat) {
      let result = config.moneyFormat;
      const activeCurrency = Shopify.currency.active;

      // Handle all amount templates
      result = result.replace(/\{\{\s*amount\s*\}\}/g, formattedPrice);
      result = result.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formattedPrice);
      result = result.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(parseFloat(formattedPrice)).toString());
      result = result.replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, formattedPrice);

      // For currencies that don't use decimals (like JPY), round to whole number
      result = result.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(parseFloat(formattedPrice)).toString());


      // Fix decimal separator for EUR currency
      if (activeCurrency === 'EUR' || activeCurrency === 'DKK') {
        // Replace dot with comma for EUR decimal separator
        result = result.replace(/(\d+)\.(\d+)/g, '$1,$2');
      }

      if (!result.includes('{{')) {
        return result;
      }
    }

    return formattedPrice;
  }

  /**
   * Format money with fallback for legacy formatMoney calls
   * @param {number|string} price - Price value to format
   * @returns {string} Formatted price string
   */
  static formatMoney(price) {
    // Try to find an algolia element on the page to get configuration
    const algoliaElement = document.querySelector('algolia-collection, algolia-predictive-search');
    return this.formatPrice(price, {}, algoliaElement);
  }

  /**
   * Get multilingual pricing configuration from data attributes or options
   * @param {HTMLElement} element - Element to read data attributes from
   * @param {Object} options - Options object
   * @returns {Object} Configuration object
   */
  static getMultilingualPricingConfig(element, options = {}) {
    const config = {
      enableMultiPricing: false,
      enableRoundMultiPrices: false,
      moneyFormat: null
    };

    // Get configuration from element data attributes
    if (element && element.dataset) {
      config.enableMultiPricing = element.dataset.enableMultiPricing === 'true';
      config.enableRoundMultiPrices = element.dataset.enableRoundMultiPrices === 'true';
      config.moneyFormat = element.dataset.moneyFormat;
    }

    // Fallback to options
    if (options.enableMultiPricing !== undefined) {
      config.enableMultiPricing = options.enableMultiPricing;
    }
    if (options.enableRoundMultiPrices !== undefined) {
      config.enableRoundMultiPrices = options.enableRoundMultiPrices;
    }
    if (options.moneyFormat) {
      config.moneyFormat = options.moneyFormat;
    }

    return config;
  }

  /**
   * Check if multilingual pricing should be applied
   * @param {Object} config - Configuration object
   * @returns {boolean} Whether to apply multilingual pricing
   */
  static shouldApplyMultilingualPricing(config) {
    // Check if multilingual pricing is enabled
    if (!config.enableMultiPricing) {
      return false;
    }

    // Check if Shopify currency object is available
    if (typeof Shopify === 'undefined' ||
        typeof Shopify.currency === 'undefined' ||
        typeof Shopify.currency.rate === 'undefined' ||
        typeof Shopify.currency.active === 'undefined') {
      return false;
    }

    return true;
  }


  /**
   * Apply currency-specific rounding rules
   * @param {number} priceInCents - Price in cents
   * @returns {number} Rounded price
   */
  static applyCurrencySpecificRounding(priceInCents, config) {
    // Check if rounding is enabled
    if (!config || !config.enableRoundMultiPrices) {
      return priceInCents;
    }

    const activeCurrency = Shopify.currency.active;

    // Convert to cents for rounding calculations
    const priceInCentsForRounding = Math.round(priceInCents * 100);

    // Round up to the 95th cent for EUR
    if (activeCurrency === 'EUR') {
      const result = parseInt(priceInCentsForRounding / 100, 10) * 100 + 95;
      return result / 100; // Convert back to main currency unit
    }
    // Round up to the 00th cent for GBP, USD, DKK
    else if (activeCurrency === 'GBP' || activeCurrency === 'USD' || activeCurrency === 'DKK') {
      const result = parseInt(priceInCentsForRounding / 100, 10) * 100 + 100;
      return result / 100; // Convert back to main currency unit
    }

    // Default: no additional rounding
    return priceInCents;
  }

  /**
   * Parse color mappings from data string
   * @param {string} colorMappingsData - Color mappings data string
   * @returns {Array} Array of color mapping objects
   */
  static parseColorMappings(colorMappingsData) {
    if (!colorMappingsData || typeof colorMappingsData !== 'string' || colorMappingsData.trim() === '') {
      return [];
    }

    try {
      const mappings = [];
      const colorEntries = colorMappingsData.split(',');

      for (const entry of colorEntries) {
        const trimmedEntry = entry.trim();
        if (trimmedEntry) {
          const parts = trimmedEntry.split('|');
          if (parts.length === 2) {
            const colorName = parts[0].trim();
            const hexColor = parts[1].trim();

            if (colorName && hexColor) {
              mappings.push({ label: colorName, color: hexColor });
            }
          }
        }
      }
      return mappings;
    } catch (error) {
      console.error('Error parsing color mappings:', error);
      return [];
    }
  }

  /**
   * Handleize a string (convert to URL-friendly format)
   * @param {string} str - String to handleize
   * @returns {string} Handleized string
   */
  static handleizeColorName(str) {
    return str.toLowerCase().replace(/[^\w\u00C0-\u024f]+/g, "-").replace(/^-+|-+$/g, "");
  }


  /**
   * Safe error logging (only in development)
   * @param {string} message - Error message
   * @param {Error} error - Error object
   */
  static logError(message, error = null) {
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
      console.error(message, error);
    }
  }

  /**
   * Get collection handle from URL
   * @returns {string|null} Collection handle or null
   */
  static getCollectionHandle() {
    const path = window.location.pathname;
    const match = path.match(/\/collections\/([^/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Update active filter count and total results display
   * @param {number} totalRefinements - Total number of active refinements
   * @param {number} totalResults - Total number of search results
   */
  static updateActiveFilterCount(totalRefinements, totalResults = null) {
    const hasRefinements = totalRefinements > 0;

    // Update the active filter count display
    const filterCountElement = document.querySelector('[data-total-refined-values]');
    if (filterCountElement) {
      filterCountElement.textContent = hasRefinements ? `(${totalRefinements})` : '';
      filterCountElement.style.display = hasRefinements ? '' : 'none';
    }

    // Update the total results display
    const totalResultElement = document.querySelector('[data-total-result]');
    if (totalResultElement && totalResults !== null) {
      const totalResultsElm = totalResultElement.getAttribute('data-total-result');
      totalResultsElm && (totalResultElement.textContent = totalResultsElm.replace('[number]', totalResults));
    }

    // Show/hide the current filters container
    const currentFiltersContainer = document.getElementById('current-filters-container');
    currentFiltersContainer && (currentFiltersContainer.style.display = hasRefinements ? 'flex' : 'none');

    // Show/hide the clear button
    const clearButton = document.querySelector('[data-clear-refinements]');
    clearButton && (clearButton.style.display = hasRefinements ? 'flex' : 'none');
  }

  /**
   * Update only the total results display (data-total-result)
   * @param {number} totalResults - Total number of search results
   */
  static updateTotalResultsOnly(totalResults) {
    // Update the total results display
    const totalResultElement = document.querySelector('[data-total-result]');
    if (totalResultElement && totalResults !== null) {
      const totalResultsElm = totalResultElement.getAttribute('data-total-result');
      totalResultsElm && (totalResultElement.textContent = totalResultsElm.replace('[number]', totalResults));
    }
  }

  /**
   * Deep clone an object
   * @param {*} obj - Object to clone
   * @returns {*} Cloned object
   */
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => AlgoliaSharedUtils.deepClone(item));
    if (typeof obj === 'object') {
      const clonedObj = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          clonedObj[key] = AlgoliaSharedUtils.deepClone(obj[key]);
        }
      }
      return clonedObj;
    }
  }

}

  // Export for use in other modules
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = AlgoliaSharedUtils;
  } else if (typeof window !== 'undefined') {
    window.AlgoliaSharedUtils = AlgoliaSharedUtils;
  }

  // Clear loading flag
  window.AlgoliaSharedUtilsLoading = false;
}
